import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CrudService } from '../shared/grid/crud.service';
import { DashboardView, DashboardViewLookup, DEFAULT_DASHBOARD_ID } from '../models/dashboard-view.model';
import { DashboardViewWidget } from '../models/dashboard-view-widget.model';

export interface DashboardViewWithWidgets extends DashboardView {
  widgets: DashboardViewWidget[];
}

interface DashboardViewWidgetDto extends Omit<DashboardViewWidget, 'configJson'> {
  configJson: string;
}

interface DashboardViewWithWidgetsDto extends Omit<DashboardViewWithWidgets, 'widgets'> {
  widgets: DashboardViewWidgetDto[];
}

@Injectable({ providedIn: 'root' })
export class DashboardViewService extends CrudService<DashboardViewWithWidgets> {
  readonly DASHBOARD_ID = DEFAULT_DASHBOARD_ID;

  constructor(http: HttpClient) {
    super('DashboardView', http);
  }

  private serializeWidgets(view: DashboardViewWithWidgets | Partial<DashboardViewWithWidgets>): DashboardViewWithWidgetsDto | Partial<DashboardViewWithWidgetsDto> {
    return {
      ...view,
      widgets: view.widgets?.map(w => ({
        ...w,
        configJson: typeof w.configJson === 'string' ? w.configJson : JSON.stringify(w.configJson || {})
      })) || []
    } as DashboardViewWithWidgetsDto;
  }

  private deserializeWidgets(view: DashboardViewWithWidgetsDto): DashboardViewWithWidgets {
    return {
      ...view,
      widgets: view.widgets?.map(w => ({
        ...w,
        configJson: typeof w.configJson === 'string' ? JSON.parse(w.configJson || '{}') : (w.configJson || {})
      })) || []
    } as DashboardViewWithWidgets;
  }

  override create(entity: DashboardViewWithWidgets | Partial<DashboardViewWithWidgets>): Observable<DashboardViewWithWidgets> {
    const serialized = this.serializeWidgets(entity);
    return this.http.post<DashboardViewWithWidgetsDto>(this.getApiUrl(`${this.controllerName}/Create`), serialized).pipe(
      map(view => this.deserializeWidgets(view))
    );
  }

  override update(entity: DashboardViewWithWidgets): Observable<Object> {
    return this.http.post(this.getApiUrl(`${this.controllerName}/Update`), this.serializeWidgets(entity));
  }

  override getById(id: string): Observable<DashboardViewWithWidgets> {
    return super.getById(id).pipe(
      map(view => this.deserializeWidgets(view as unknown as DashboardViewWithWidgetsDto))
    );
  }

  getDefaultView(): Observable<DashboardViewWithWidgets | null> {
    const params = new HttpParams().set('dashboardId', this.DASHBOARD_ID);
    return this.http.get<DashboardViewWithWidgetsDto | null>(this.getApiUrl('DashboardView/GetDefaultView'), { params }).pipe(
      map(view => view ? this.deserializeWidgets(view) : null)
    );
  }

  override getAll(filterBy?: string): Observable<DashboardViewLookup[]> {
    const params = filterBy ? { filterBy } : {};
    return this.http.get<DashboardViewLookup[]>(this.getApiUrl('DashboardView/GetAll'), { params });
  }

  rename(id: string, viewName: string): Observable<void> {
    return this.http.patch<void>(this.getApiUrl(`${this.controllerName}/Patch/${id}`), { viewName });
  }
}